import type { CrremPathwayChartPayload, CrremYearlyPoint } from "../model/types";

import { isExcludedCrremChartSeries } from "../model/constants";
import { formatCrremScenarioLabel } from "./formatScenarioLabel";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const readString = (value: unknown) => (typeof value === "string" && value.length > 0 ? value : undefined);

const isYearlyPoint = (value: unknown): value is CrremYearlyPoint => {
  if (!isRecord(value)) return false;
  return (
    typeof value.year === "number" &&
    Number.isFinite(value.year) &&
    typeof value.value === "number" &&
    Number.isFinite(value.value)
  );
};

export const parseYearlyPoints = (value: unknown): CrremYearlyPoint[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter(isYearlyPoint)
    .map((point) => ({ value: point.value, year: point.year }))
    .sort((left, right) => left.year - right.year);
};

const parseMissingFields = (value: unknown) =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && item.length > 0) : [];

export const readAxiosErrorData = (error: unknown) => {
  if (!isRecord(error) || !isRecord(error.response)) return undefined;
  return error.response.data;
};

export const formatCrremSubsetUploadError = (error: unknown, fallback: string) => {
  const data = readAxiosErrorData(error);
  if (!isRecord(data)) return fallback;
  const messages: string[] = [];
  const summary = readString(data.error);
  if (summary) messages.push(summary);
  messages.push(...parseMissingFields(data.errors).slice(0, 3));
  return messages.length ? messages.join("\n") : fallback;
};

export const parseCrremPathwayIncomplete = (payload: unknown) => {
  if (!isRecord(payload) || payload.status !== "incomplete") return undefined;
  const missing = parseMissingFields(payload.missing);
  if (missing.length === 0) return undefined;
  return { missing };
};

export const parseCrremPathwayChart = (payload: unknown): CrremPathwayChartPayload => {
  const incomplete = parseCrremPathwayIncomplete(payload);
  if (incomplete) return { missing: incomplete.missing, scenarios: [], unit: "" };
  if (!isRecord(payload) || !isRecord(payload.series)) return { missing: [], scenarios: [], unit: "" };
  const scenarios = Object.entries(payload.series)
    .filter(([key]) => !isExcludedCrremChartSeries(key))
    .map(([key, points]) => ({
      key,
      label: formatCrremScenarioLabel(key),
      points: parseYearlyPoints(points),
    }));
  return {
    missing: [],
    scenarios,
    unit: readString(payload.unit) ?? "",
  };
};
